import { Component, effect, inject, Input, OnInit, WritableSignal } from '@angular/core';
import {MatIcon} from "@angular/material/icon";
import {MatRipple} from "@angular/material/core";
import {TranslateModule} from "@ngx-translate/core";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatButton, MatIconButton} from "@angular/material/button";
import {StoredSearchServiceImplementation} from "../../stored-search.service";
import {MatActionList, MatListItem} from "@angular/material/list";
import {SearchContainer} from "../../../core/definitions/search-container";
import {MatCheckbox} from "@angular/material/checkbox";
import {FormsModule} from "@angular/forms";
import {StoredSearch} from "../../../core/definitions/stored-search";
import {SearchHandlerService} from "../../search-handler.service";
import {CommonsService} from "../../../core/commons.service";
import {MatDialog} from "@angular/material/dialog";
import {StoredSearchDialogComponent} from "./saved-search-dialog/stored-search-dialog.component";
import {NgClass} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
import {AppNotification, NotificationService} from "../../../shared/notification.service";

interface NewStoredSearchData {
  givenName: string;
  setAsDefault?: boolean;
  setOnClose?: boolean;
}

const NAME_EXISTS_MESSAGE = 'Name already exists';
const NAME_NOT_UNIQUE_KEY = 'TRANS__FILTER_MENU__MUST_BE_UNIQUE';
const SAVE_FAILED_KEY = 'TRANS__FILTER_MENU__STORED_SEARCH_SAVE_FAILED';

@Component({
    selector: 'app-filter-menu-stored-search-selector',
    imports: [
        MatIcon,
        MatRipple,
        TranslateModule,
        MatMenu,
        MatMenuTrigger,
        MatButton,
        MatActionList,
        MatListItem,
        MatCheckbox,
        FormsModule,
        MatIconButton,
        MatMenuItem,
        NgClass,
        MatTooltip
    ],
    templateUrl: './filter-menu-stored-search-selector.component.html',
    styleUrls: ['./filter-menu-stored-search-selector.component.scss']
})
export class FilterMenuStoredSearchSelectorComponent implements OnInit {
  // Injections
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly matDialog: MatDialog = inject(MatDialog);
  private readonly searchHandlerService: SearchHandlerService = inject(SearchHandlerService);
  private readonly notificationService: NotificationService = inject(NotificationService);

  @Input() fsi: StoredSearchServiceImplementation;
  @Input() searchContainer: SearchContainer;
  @Input() trigger: WritableSignal<boolean>;
  @Input() disabled: boolean;

  currentMaxListLength = 10;
  currentTriggerValue = false;
  displayList: Array<StoredSearch> = [];

  DEFAULT_MAX_LIST_LENGTH = 10;

  constructor() {
    effect(() => {
      if (this.currentTriggerValue !== this.trigger()) {
        this.currentMaxListLength = this.DEFAULT_MAX_LIST_LENGTH;

        this.currentTriggerValue = this.trigger();
      }
    });
  }

  ngOnInit() {
    if (!this.fsi.getCurrentStoredSearch()) {
      this.setCurrentStoredSearch(this.fsi.createNewStoredSearch());
    }

    if (this.fsi.getStoredSearches()) {
      this.getSetCurrentStoredSearch();
    }

    this.updateDisplayList();
  }

  activeStoredSearchHasChanges(): boolean {
    if (this.searchContainer.storedSearch.curStoredSearchId) {
      return this.fsi.storedSearchHasChanges(this.searchContainer.storedSearch.curStoredSearch);
    }

    return false;
  }

  checkboxClicked(event: MouseEvent): void {
    event.stopPropagation();

    this.fsi.toggleStoredSearchIsDefault(this.searchContainer.storedSearch.curStoredSearch);
    this.fsi.createOrUpdateStoredSearch(this.searchContainer.storedSearch.curStoredSearch, this.searchContainer.storedSearch.curStoredSearch.private).then();
  }

  async clearSetStoredSearch(event: MouseEvent) {
    event.stopPropagation();

    if (this.searchContainer.storedSearch.curStoredSearchId) {
      this.searchContainer.storedSearch.curStoredSearchId = null;
      this.searchContainer.storedSearch.curStoredSearch = null;
      this.searchContainer.storedSearch.curStoredSearchName = null;

      this.setCurrentStoredSearch(this.fsi.createNewStoredSearch());

      await this.searchHandlerService.goPathView(this.searchContainer.path, this.searchContainer);
    }
  }

  createStoredSearchFromSearch(event: MouseEvent) {
    event.stopPropagation();

    const dialogRef = this.matDialog.open(StoredSearchDialogComponent, {
      data: {
        editMode: false,
        fsi: this.fsi
      }
    });

    dialogRef.afterClosed().subscribe(async (data) => {
      if (data) {
        await this.saveNewStoredSearch(data);
      }
    });
  }

  async saveStoredSearchChanges() {
    try {
      await this.fsi.createOrUpdateStoredSearch(this.searchContainer.storedSearch.curStoredSearch);
    } catch (e) {
      console.error(e);
    }
  }

  async deleteStoredSearch(event: MouseEvent, storedSearch: StoredSearch) {
    event.stopPropagation();

    const storedSearchId = storedSearch.storedSearchId;

    await this.fsi.deleteStoredSearch(storedSearchId);

    this.updateDisplayList();

    if (storedSearchId === this.searchContainer.storedSearch.curStoredSearchId) {
      this.searchContainer.storedSearch.curStoredSearchId = null;
    }

    if (this.searchContainer.storedSearch.curStoredSearchId === null) {
      this.searchContainer.storedSearch.curStoredSearch = null;
      this.searchContainer.storedSearch.curStoredSearchName = null;
      await this.searchHandlerService.goPathView(this.searchContainer.path, this.searchContainer);
    }

    if (!this.fsi.getCurrentStoredSearch()) {
      this.setCurrentStoredSearch(this.fsi.createNewStoredSearch());
    }
  }

  editStoredSearch(event: MouseEvent, storedSearch: StoredSearch) {
    event.stopPropagation();

    const dialogRef = this.matDialog.open(StoredSearchDialogComponent, {
      data: {
        currentName: storedSearch.stored_search.name,
        editMode: true,
        fsi: this.fsi
      }
    });

    dialogRef.afterClosed().subscribe(async (data) => {
      if (data) {
        if (storedSearch.stored_search.stored) {
          storedSearch.stored_search.name = data.givenName;

          try {
            await this.fsi.createOrUpdateStoredSearch(storedSearch);

            this.updateDisplayList();
            this.setStoredSearch(storedSearch, event).then();
          }
          catch(error) {
            console.error(error);
          }
        }
      }
    });
  }

  itemActionButtonClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  async setStoredSearch(storedSearch: StoredSearch, event: MouseEvent) {
    event.stopPropagation();

    if (storedSearch) {
      await this.fsi.loadStoredSearches();
      this.setCurrentStoredSearch(storedSearch);
      await this.searchHandlerService.goPathView(storedSearch.stored_search.homePath, this.searchContainer, true);
    }
  }

  setSearchFromStoredSearch(storedSearch: StoredSearch): void {
    let pathParams = null;

    for (const key of Object.keys(storedSearch.stored_search.pathParams)) {
      if (storedSearch.stored_search.pathParams[key] !== null) {
        pathParams = storedSearch.stored_search.pathParams[key];
      }
    }

    if (pathParams !== null) {
      if (pathParams.hasOwnProperty('filtersFacets.checkedFilters')) {
        this.searchContainer.filtersFacets.checkedFilters = {};

        for (const key of Object.keys(pathParams['filtersFacets.checkedFilters'])) {
          this.searchContainer.filtersFacets.checkedFilters[key] = [true];
        }

        this.searchContainer.filtersFacets.checkedFilters = pathParams['filtersFacets.checkedFilters'];
      }

      if (pathParams.hasOwnProperty('advancedSearchParams')) {
        this.searchContainer.extendedSearchParams = pathParams.advancedSearchParams;
      }

      if (pathParams.hasOwnProperty('queryContainer.query')) {
        this.searchContainer.queryContainer.query = pathParams['queryContainer.query']
      }

      this.searchHandlerService.goPathView(storedSearch.stored_search.homePath, this.searchContainer, true).then();
    }
  }

  showMoreOfList(event: MouseEvent) {
    event.stopPropagation();

    if (this.currentMaxListLength <= this.displayList.length) {
      this.currentMaxListLength += this.DEFAULT_MAX_LIST_LENGTH;
    }
  }

  private async saveNewStoredSearch(data: NewStoredSearchData) {
    const current: StoredSearch = this.searchContainer.storedSearch.curStoredSearch;

    this.applyNewSearchSettings(current, data);

    try {
      const savedStoredSearch: StoredSearch = await this.persistStoredSearch(current, data);

      if (!savedStoredSearch) {
        this.notify(SAVE_FAILED_KEY, 'error');
        return;
      }

      await this.activateAfterSave(savedStoredSearch, current, data.setOnClose);
    }
    catch (error) {
      console.error(error);
      this.notifyIfNameExists(error);
    }
  }

  private applyNewSearchSettings(current: StoredSearch, data: NewStoredSearchData) {
    if (current.storedSearchId) {
      return;
    }

    current.stored_search.name = data.givenName;

    if (data.setAsDefault) {
      current.stored_search.isDefault = true;
    }
  }

  private persistStoredSearch(current: StoredSearch, data: NewStoredSearchData): Promise<StoredSearch> {
    return current.storedSearchId
      ? this.fsi.storeStoredSearchAsNewStoredSearch(current, data.givenName, !!data.setAsDefault)
      : this.fsi.createOrUpdateStoredSearch(current);
  }

  private async activateAfterSave(savedStoredSearch: StoredSearch, previous: StoredSearch, setOnClose: boolean) {
    if (setOnClose) {
      this.setCurrentStoredSearch(savedStoredSearch);
    }
    else if (!previous.storedSearchId) {
      this.setCurrentStoredSearch(this.fsi.createNewStoredSearch());
    }
    else {
      this.setCurrentStoredSearch(this.fsi.getStoredSearch(previous.storedSearchId) ?? previous);
    }

    this.updateDisplayList();

    if (setOnClose) {
      await this.searchHandlerService.goPathView(savedStoredSearch.stored_search.homePath, this.searchContainer, true);
    }
  }

  // API failures are already reported by the global CMS error handler.
  private notifyIfNameExists(error: Error) {
    if (error?.message === NAME_EXISTS_MESSAGE) {
      this.notify(NAME_NOT_UNIQUE_KEY, 'info');
    }
  }

  private notify(translationKey: string, type: 'info' | 'error') {
    this.notificationService.addNotification(new AppNotification([translationKey], type));
  }

  private getSetCurrentStoredSearch() {
    this.setCurrentStoredSearch(this.fsi.getCurrentStoredSearch());
  }

  private setCurrentStoredSearch(storedSearch: StoredSearch) {
    this.fsi.setCurrentStoredSearch(storedSearch ? storedSearch.storedSearchId : null);

    this.searchContainer.storedSearch.curStoredSearch = this.commons.copy(storedSearch);
  }

  private updateDisplayList() {
    this.displayList = this.commons.copy(this.fsi.getStoredSearches());
  }
}
